/**
 * Customers Service
 * Handles all customer-related API calls
 */

import { apiService, ApiResponse } from './api.service';

export type SubscriptionFrequency = 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
export type TransactionType = 'PAYMENT' | 'REFUND' | 'CREDIT' | 'CHARGE';

/**
 * Customer Transaction Interface
 */
export interface CustomerTransaction {
  id: string;
  customerId: string;
  tenantId: string;
  type: TransactionType;
  amount: number | string;
  description: string;
  notes: string | null;
  receiptNumber: string;
  transactionDate: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create Transaction DTO
 */
export interface CreateTransactionDto {
  type: TransactionType;
  amount: number;
  description: string;
  notes?: string;
  transactionDate?: string;
}

/**
 * Customer Interface — matches the backend Prisma Customer model
 */
export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  dateOfBirth: string | null;
  membershipTier: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM';
  loyaltyPoints: number;
  totalSpent: number | string;
  totalOrders: number;
  isActive: boolean;
  tags: string[];
  notes: string | null;
  preferences: any;
  customerTypeId: string | null;
  customerNumber: string | null;
  customerType: { id: string; name: string; isSubscriptionBased: boolean; numberPrefix: string } | null;
  subscriptionFrequency: SubscriptionFrequency | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  nextPaymentDate: string | null;
  subscriptionPlan: string | null;
  subscriptionPlanId: string | null;
  branchId: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  _count?: { orders: number };
  orders?: CustomerOrder[];
}

export interface CustomerOrder {
  id: string;
  orderNumber: string;
  orderDate: string;
  status: string;
  total: number | string;
  items: any[];
}

/**
 * Create Customer DTO
 */
export interface CreateCustomerDto {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  membershipTier?: string;
  loyaltyPoints?: number;
  branchId?: string;
  customerTypeId?: string;
  subscriptionFrequency?: SubscriptionFrequency;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  nextPaymentDate?: string;
  subscriptionPlan?: string;
}

/**
 * Update Customer DTO
 */
export interface UpdateCustomerDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  membershipTier?: string;
  loyaltyPoints?: number;
  isActive?: boolean;
}

/**
 * Customers Query Parameters
 */
export interface CustomersQuery {
  page?: number;
  limit?: number;
  search?: string;
  branchId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated Customers Response — matches backend meta structure
 */
export interface PaginatedCustomers {
  data: Customer[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Customer Statistics
 */
export interface CustomerStats {
  totalCustomers: number;
  activeCustomers: number;
  membersByTier: { membershipTier: string; _count: number }[];
}

/**
 * Customers Service
 */
class CustomersService {
  /**
   * Get all customers with pagination and filters
   */
  async getCustomers(query?: CustomersQuery): Promise<ApiResponse<PaginatedCustomers>> {
    try {
      const params = new URLSearchParams();
      if (query?.page) params.append('page', query.page.toString());
      if (query?.limit) params.append('limit', query.limit.toString());
      if (query?.search) params.append('search', query.search);
      if (query?.branchId) params.append('branchId', query.branchId);
      if (query?.sortBy) params.append('sortBy', query.sortBy);
      if (query?.sortOrder) params.append('sortOrder', query.sortOrder);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiService.get<PaginatedCustomers>(`/customers${queryString}`);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      throw error;
    }
  }

  /**
   * Get single customer by ID
   */
  async getCustomerById(id: string): Promise<ApiResponse<Customer>> {
    try {
      return await apiService.get<Customer>(`/customers/${id}`);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      throw error;
    }
  }

  /**
   * Create new customer
   */
  async createCustomer(data: CreateCustomerDto): Promise<ApiResponse<Customer>> {
    try {
      return await apiService.post<Customer>('/customers', data);
    } catch (error) {
      console.error('Failed to create customer:', error);
      throw error;
    }
  }

  /**
   * Update customer
   */
  async updateCustomer(id: string, data: UpdateCustomerDto): Promise<ApiResponse<Customer>> {
    try {
      return await apiService.patch<Customer>(`/customers/${id}`, data);
    } catch (error) {
      console.error('Failed to update customer:', error);
      throw error;
    }
  }

  /**
   * Delete customer
   */
  async deleteCustomer(id: string): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>(`/customers/${id}`);
    } catch (error) {
      console.error('Failed to delete customer:', error);
      throw error;
    }
  }

  /**
   * Get customer statistics
   */
  async getCustomerStats(): Promise<ApiResponse<CustomerStats>> {
    try {
      return await apiService.get<CustomerStats>('/customers/stats');
    } catch (error) {
      console.error('Failed to fetch customer stats:', error);
      throw error;
    }
  }

  async recalculateAllLoyalty(): Promise<ApiResponse<{ message: string; updatedCount: number }>> {
    try {
      return await apiService.post<{ message: string; updatedCount: number }>('/customers/recalculate-loyalty', {});
    } catch (error) {
      console.error('Failed to recalculate loyalty tiers:', error);
      throw error;
    }
  }

  async resetAllLoyaltyPoints(): Promise<ApiResponse<{ message: string }>> {
    try {
      return await apiService.post<{ message: string }>('/customers/reset-loyalty', {});
    } catch (error) {
      console.error('Failed to reset loyalty points:', error);
      throw error;
    }
  }

  /**
   * Get customer orders
   */
  async getCustomerOrders(customerId: string, page = 1, limit = 10): Promise<ApiResponse<any>> {
    try {
      return await apiService.get(`/customers/${customerId}/orders?page=${page}&limit=${limit}`);
    } catch (error) {
      console.error('Failed to fetch customer orders:', error);
      throw error;
    }
  }

  async getTransactions(customerId: string): Promise<ApiResponse<CustomerTransaction[]>> {
    try {
      return await apiService.get<CustomerTransaction[]>(`/customers/${customerId}/transactions`);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      throw error;
    }
  }

  async createTransaction(customerId: string, data: CreateTransactionDto): Promise<ApiResponse<CustomerTransaction>> {
    try {
      return await apiService.post<CustomerTransaction>(`/customers/${customerId}/transactions`, data);
    } catch (error) {
      console.error('Failed to create transaction:', error);
      throw error;
    }
  }

  async emailTransactionReceipt(customerId: string, transactionId: string): Promise<ApiResponse<void>> {
    try {
      return await apiService.post<void>(`/customers/${customerId}/transactions/${transactionId}/email-receipt`, {});
    } catch (error) {
      console.error('Failed to email receipt:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const customersService = new CustomersService();
